import * as Ids from '@utils/ids';

import PushCode from './pushCode';

type ArbitraryObject = { [index: string]: any };

type RawMessage = { code: number | string } & ArbitraryObject;

const currentLoginWmPoiId = Ids.getWmPoiId();

/**
 * 前端封装过的通用 Push 消息对象
 */
class PushMessage<T extends ArbitraryObject = ArbitraryObject> {
    /** 此消息的 Push Code */
    readonly code: PushCode;

    /** 此消息归属的门店ID（不是所有的消息都有门店信息，默认值："unknown"） */
    readonly wmPoiId: string = 'unknown';

    /** 该消息的业务数据部分 */
    readonly data: T;

    /**
     * 原始消息，向内层透传时使用
     * TODO: 是否有必要？
     */
    readonly originalMesssage: any;

    constructor(rawMessage: RawMessage) {
        const { code, wmPoiId, wm_poi_id, poiId, poi_id, ...restFields } = rawMessage;

        this.code = Number(code);
        // 不是亲眼看到，我也不信
        const wmPoiIdInMessage = wmPoiId || wm_poi_id || poiId || poi_id;
        if (wmPoiIdInMessage) {
            this.wmPoiId = `${wmPoiIdInMessage}`;
        }

        this.data = restFields as T;

        this.originalMesssage = {
            ...rawMessage,
            handlerType: 'fromPushMessage',
        };
    }

    /**
     * 判断该消息是否可被当前门店处理（消息无门店信息/当前处于总店模式/当前单店模式且消息属于当前门店）
     */
    canBeHandledByCurrentPoi(): boolean {
        return (
            this.wmPoiId === 'unknown' ||
            this.wmPoiId === currentLoginWmPoiId ||
            currentLoginWmPoiId === '-1'
        );
    }

    /**
     * 判断该消息是否可被当前**单店**处理
     */
    canBeHandledByCurrentSinglePoi(): boolean {
        return this.wmPoiId === currentLoginWmPoiId;
    }
}

/**
 * 统一触达消息的数据结构（经过PushClient统一处理后的结构，也即不包含 code、wmPoiId字段）
 *
 * https://km.sankuai.com/page/1354776123
 */
type UnifiedBusinessMessageNoticeData = {
    /** 消息traceId */
    wmEPoiMsgId: number;
    /** 消息traceId */
    wmEMsgTraceId: number;
    /** 消息id */
    id: number;
    /** 模板名 */
    templateName: string;
    /** 是否展示站内push信息（弱提醒） */
    showInternalPush: boolean;
    /** 站内push信息 */
    internalPushInfo: {
        /** 标题 */
        title: string;
        /** 内容 */
        content: string;
        /** 链接URL */
        linkUrl?: string;
    };

    /** 是否展示文字弹窗(强提醒) */
    showDialog: boolean;
    /** 文字弹窗信息 */
    dialogInfo: {
        /** 标题 */
        title: string;
        /** 内容 */
        content: string;
        /** 链接URL */
        linkUrl?: string;
        /** 左按钮文本 */
        leftButtonText: string;
        /** 右按钮文本 */
        rightButtonText: string;
        /** 弹窗类型： 0-跳转链接弹窗 1-跳转站内信弹窗 2-功能定制弹窗 */
        dialogType: 0 | 1 | 2;
        /** 左按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
        leftBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
        /** 右按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
        rightBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
        leftBtnConfig: string;
        rightBtnConfig: string;
        utmContent: string;
        buttonCnt?: number;
    };
    /** 是否播放铃声 */
    soundBroad: boolean;
    soundInfo: {
        /** 铃声文件地址 */
        linkUrl: string;
        /** 铃声优先级 */
        priority: number;
    };
    /** 如果是1，那么就只在当前门店id = 消息中的wmPoiId的时候展示弹窗 */
    poiOnly?: 0 | 1;
};

/**
 * 轮询接口消息体
 *
 * https://km.sankuai.com/collabpage/2066525934#b-2cb1259d0ed144ecaa7ba94d3d2c50d9
 */
type PollerMesssage = {
    /** 批次id */
    wmEMsgTraceId: number;
    /** 消息id */
    wmEPoiMsgId: number;
    /** 门店 ID */
    wmPoiId: number;
    /** 账号 ID */
    acctId: number;
    /** 模板名称 */
    templateName: string;
    /** 标题 */
    title: string;
    /** 内容 */
    content: string;
    /** 链接URL */
    linkUrl: string;
    /** 左按钮文本 */
    leftButtonText: string;
    /** 右按钮文本 */
    rightButtonText: string;
    /** 弹窗类型： 0-跳转链接弹窗 1-跳转站内信弹窗 2-功能定制弹窗 */
    dialogType: 0 | 1 | 2;
    /** 左按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
    leftBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
    /** 右按钮类型 用于上报埋点统计： CANCEL-关闭｜CONFIRM-已读｜CONVERT-转化 */
    rightBtnType: 'CANCEL' | 'CONFIRM' | 'CONVERT';
    /** 
     * 左按钮配置，JSON字符串，解析后结果如下
     * 
```js
    {
        type: 1 | 2, // 1-关闭 2-操作
        succToast?: string, // 操作成功时的toast，如果未配置，则使用接口返回的结果
        failToast?: string, // 操作失败时的toast，如果未配置，则使用接口返回的结果
        url?: string, // type=2时存在，app和pc点击按钮之后调用的请求url
        appUrl?: string, // type=2时存在，app点击按钮之后调用的请求url，app端优先选择appUrl，若appUrl不存在则选择url
        pcUrl?: string, // type=2时存在，pc点击按钮之后调用的请求url，pc端优先选择pcUrl，若pcUrl不存在则选择url
    }
```
     * 
     * */
    leftBtnConfig: string;
    /** 
     * 右按钮配置，JSON字符串，解析后结果如下
     * 
```js
    {
        type: 1 | 2, // 1-关闭 2-操作
        succToast?: string, // 操作成功时的toast，如果未配置，则使用接口返回的结果
        failToast?: string, // 操作失败时的toast，如果未配置，则使用接口返回的结果
        url?: string, // type=2时存在，app和pc点击按钮之后调用的请求url
        appUrl?: string, // type=2时存在，app点击按钮之后调用的请求url，app端优先选择appUrl，若appUrl不存在则选择url
        pcUrl?: string, // type=2时存在，pc点击按钮之后调用的请求url，pc端优先选择pcUrl，若pcUrl不存在则选择url
    }
```
     * 
     * */
    rightBtnConfig: string;
    /** 透传埋点 */
    utmContent: string;
    buttonCnt?: number;
};

export default PushMessage;
export { RawMessage, ArbitraryObject, UnifiedBusinessMessageNoticeData, PollerMesssage };
